import { create, type StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { AdminUser, RoleName, UserStatus } from '../lib/admin.schema';
import {
  adminApi,
  managerApi,
  type ManagementApi,
  refetchUsers,
  type UpdateUserInput,
} from './management.service';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { formatError } from '@/shared/lib/format-error';

const getApi = (): ManagementApi => {
  return useAuthStore.getState().hasRole('admin') ? adminApi : managerApi;
};

export type UserAction =
  | { type: 'updateUser'; input: UpdateUserInput }
  | { type: 'updateStatus'; email: string; status: UserStatus }
  | { type: 'toggleOrg'; email: string; slug: string; remove: boolean }
  | { type: 'setRole'; email: string; role: RoleName }
  | { type: 'delete'; email: string }
  | { type: 'createUser'; input: UserForm };

export interface UserForm {
  email: string;
  withPassword: boolean;
  password: string;
  confirmPassword: string;
  display_name: string;
  phone: string;
  organization: string;
  role: RoleName;
}

const getDefaultOrg = (): string => {
  const orgs = useAuthStore.getState().getUserOrganizations();
  return orgs[0] ?? 'els';
};

const DEFAULT_FORM: UserForm = {
  email: '',
  withPassword: true,
  password: '',
  confirmPassword: '',
  organization: 'els',
  display_name: '',
  phone: '',
  role: 'operator',
};

const userToForm = (user: AdminUser): UserForm => ({
  email: user.email,
  withPassword: true,
  password: '',
  confirmPassword: '',
  display_name: user.display_name ?? '',
  phone: user.phone ?? '',
  organization: user.organizations[0] ?? 'els',
  role: (user.role as RoleName | null) ?? 'operator',
});

type ModalState = { type: 'create' } | { type: 'edit'; email: string } | null;

interface AdminUserState {
  modal: ModalState;
  openCreate: () => void;
  openEdit: (user: AdminUser) => void;
  closeModal: () => void;

  deleteEmail: string | null;
  openDeleteConfirm: (email: string) => void;
  closeDeleteConfirm: () => void;

  form: UserForm;
  setFormField: <K extends keyof UserForm>(field: K, value: UserForm[K]) => void;

  pendingAction: UserAction['type'] | null;
  error: string | undefined;
  act: (action: UserAction) => void;
}

// Refresh the mounted user list + stats through the shared Apollo cache (admin or manager docs,
// whichever the current role watches). Awaited so a refetch failure lands in run()'s try/catch.

const createAdminUserSlice: StateCreator<
  AdminUserState,
  [['zustand/immer', never]],
  [],
  AdminUserState
> = (set) => ({
  modal: null,
  openCreate: () => {
    set((state) => {
      state.modal = { type: 'create' };
      state.form = { ...DEFAULT_FORM, organization: getDefaultOrg() };
      state.error = undefined;
    });
  },
  openEdit: (user) => {
    set((state) => {
      state.modal = { type: 'edit', email: user.email };
      state.form = userToForm(user);
      state.error = undefined;
    });
  },
  closeModal: () => {
    set((state) => {
      state.modal = null;
      state.form = { ...DEFAULT_FORM };
      state.error = undefined;
    });
  },

  deleteEmail: null,
  openDeleteConfirm: (email) => {
    set((state) => {
      state.deleteEmail = email;
    });
  },
  closeDeleteConfirm: () => {
    set((state) => {
      state.deleteEmail = null;
    });
  },

  form: { ...DEFAULT_FORM },
  setFormField: (field, value) => {
    set((state) => {
      state.form[field] = value;
    });
  },

  pendingAction: null,
  error: undefined,
  act: (action) => {
    const token = useAuthStore.getState().session?.access_token;
    if (!token) {
      set((state) => {
        state.error = 'Your session may have expired. Sign in again.';
      });
      return;
    }

    set((state) => {
      state.pendingAction = action.type;
      state.error = undefined;
    });

    const api = getApi();

    const run = async () => {
      try {
        switch (action.type) {
          case 'updateUser': {
            const result = await api.updateUser(action.input);
            set((state) => {
              if (state.modal?.type === 'edit') {
                state.modal.email = result.email;
              }
            });
            break;
          }
          case 'updateStatus':
            await api.updateUser({ email: action.email, status: action.status });
            break;
          case 'toggleOrg':
            if (action.remove) {
              await api.removeOrgMember(action.slug, action.email);
            } else {
              await api.addOrgMember(action.slug, action.email);
            }
            break;
          case 'setRole':
            await api.setRole(action.email, action.role);
            break;
          case 'delete':
            await api.deleteUser(action.email);
            set((state) => {
              state.deleteEmail = null;
              if (state.modal?.type === 'edit' && state.modal.email === action.email) {
                state.modal = null;
              }
            });
            break;
          case 'createUser': {
            // Pick only server fields — `confirmPassword`/`withPassword` are client-only and the
            // gateway's CreateUserInput rejects unknown fields (GraphQL strict validation).
            const { email, withPassword, password, organization, display_name, phone, role } = action.input;
            await api.createUser({
              email,
              password: withPassword ? password : undefined,
              organization,
              display_name,
              phone,
              role,
            });
            set((state) => {
              state.modal = null;
            });
            break;
          }
        }
        await refetchUsers();
      } catch (err) {
        set((state) => {
          state.error = formatError(err);
        });
      } finally {
        set((state) => {
          state.pendingAction = null;
        });
      }
    };

    void run();
  },
});

export const useAdminUserStore = create<AdminUserState>()(
  devtools(immer(createAdminUserSlice), { name: 'admin-user-store' }),
);
